import { ARCH_HEIGHT_DATA_ORDERED } from "../../../constants/foot";
import StudioSlot from "../../insole-studio/StudioSlot";

export type FootSide = 'left' | 'right';

interface ArchHeightTestControlsProps {
  /** Value the dropdowns start on — normally the "not scanned" bucket. */
  defaultArchIndex: number;
  onChange: (side: FootSide, archIndex: number) => void;
}

/**
 * Dev-only overlay for driving each foot's arch height by hand, so the bulge
 * animation can be checked without a real scan.
 *
 * Renders nothing outside dev. `import.meta.env.DEV` is statically replaced at
 * build time, so the early return collapses to `return null` in production.
 *
 * Portals into the same "canvasOverlay" slot as ProgressControl —
 * StudioSlot uses createPortal, which appends, so the two coexist.
 */
export const ArchHeightTestControls = ({
  defaultArchIndex,
  onChange,
}: ArchHeightTestControlsProps) => {
  if (!import.meta.env.DEV) return null;

  return (
    <StudioSlot name="canvasOverlay">
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 20,
          // The overlay container may not take pointer events itself.
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          padding: '10px 12px',
          borderRadius: 6,
          background: 'rgba(0, 0, 0, 0.72)',
          color: '#fff',
          font: '12px/1.4 system-ui, sans-serif',
        }}
      >
        <strong style={{ fontWeight: 600 }}>Arch height (dev)</strong>
        {(['left', 'right'] as const).map((side) => (
          <label
            key={side}
            style={{ display: 'flex', gap: 6, alignItems: 'center' }}
          >
            <span style={{ width: 36, textTransform: 'capitalize' }}>{side}</span>
            <select
              defaultValue={defaultArchIndex}
              onChange={(e) => onChange(side, Number(e.currentTarget.value))}
              style={{ font: 'inherit' }}
            >
              {ARCH_HEIGHT_DATA_ORDERED.map(({ bucket, type }) => (
                <option key={bucket} value={bucket}>
                  {bucket} — {type}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </StudioSlot>
  );
};

export default ArchHeightTestControls;
